// Cálculo de la tabla de posiciones de una zona, acumulando los partidos jugados
// desde la primera fecha hasta la fecha seleccionada (inclusive).
//
// Criterio de orden:
//   1) Puntos (ganado 3, empatado 1, perdido 0)
//   2) Partidos ganados
//   3) Diferencia de goles
//   4) Goles a favor
//   5) Head-to-head (resultado entre los equipos igualados)
// Si aún persiste la igualdad, esas filas quedan marcadas con `tied: true`.
//
// Caso especial: si los DOS equipos dieron walk-over el partido igual cuenta como
// jugado y lo pierden los dos (PJ y PP para cada uno, sin puntos). Cada equipo
// recibe 3 goles en contra y ninguno suma goles a favor.
//
// El orden de fechas (y con él la numeración "Fecha 1", "Fecha 2", ...) sale de
// components/torneos/fechas.js, para que acumular "hasta la Fecha N" signifique
// lo mismo que el rótulo que se ve en los listados.

import { ordenarFechas } from "components/torneos/fechas";
import { GOLES_WO, esWODoble, marcadorDePartido } from "components/torneos/resultado";

function emptyRow(teamId) {
  return { teamId, pj: 0, pg: 0, pe: 0, pp: 0, gf: 0, gc: 0, dif: 0, pts: 0, tied: false };
}

// Devuelve las filas ordenadas: [{ teamId, pj, pg, pe, pp, gf, gc, dif, pts }]
export function computeStandings(zona, upToFechaId) {
  const teamIds = zona.teamIds || [];
  const ordered = ordenarFechas(zona.matchDates || []);
  const idx = ordered.findIndex((f) => f.id === upToFechaId);
  const included = idx >= 0 ? ordered.slice(0, idx + 1) : ordered;

  // Partidos jugados con ambos equipos definidos. El marcador sale de
  // `marcadorDePartido`, que ya contempla el 3 a 0 de los walk-over.
  // `doble` marca el WO de los dos equipos: el 0 a 0 no es empate, pierden los dos.
  const matches = [];
  included.forEach((f) =>
    (f.matches || []).forEach((m) => {
      if (m.jugado && m.homeTeamId && m.awayTeamId) {
        const marcador = marcadorDePartido(m);
        matches.push({
          home: m.homeTeamId,
          away: m.awayTeamId,
          hg: marcador.local,
          ag: marcador.visitante,
          doble: esWODoble(m),
        });
      }
    })
  );

  const stats = {};
  teamIds.forEach((id) => (stats[id] = emptyRow(id)));
  const ensure = (id) => (stats[id] ? stats[id] : (stats[id] = emptyRow(id)));

  matches.forEach((m) => {
    const h = ensure(m.home);
    const a = ensure(m.away);
    h.pj++;
    a.pj++;
    // WO de los dos: partido perdido para ambos, con 3 goles en contra cada uno.
    if (m.doble) {
      h.gc += GOLES_WO;
      a.gc += GOLES_WO;
      h.pp++;
      a.pp++;
      return;
    }
    h.gf += m.hg;
    h.gc += m.ag;
    a.gf += m.ag;
    a.gc += m.hg;
    if (m.hg > m.ag) {
      h.pg++;
      h.pts += 3;
      a.pp++;
    } else if (m.hg < m.ag) {
      a.pg++;
      a.pts += 3;
      h.pp++;
    } else {
      h.pe++;
      a.pe++;
      h.pts++;
      a.pts++;
    }
  });

  const rows = Object.values(stats);
  rows.forEach((s) => (s.dif = s.gf - s.gc));
  rows.sort((a, b) => b.pts - a.pts || b.pg - a.pg || b.dif - a.dif || b.gf - a.gf);
  applyHeadToHead(rows, matches);
  return rows;
}

const baseEqual = (a, b) => a.pts === b.pts && a.pg === b.pg && a.dif === b.dif && a.gf === b.gf;
const miniEqual = (a, b) => a.pts === b.pts && a.dif === b.dif && a.gf === b.gf;

// Desempata por head-to-head los grupos con igual (pts, pg, dif, gf).
// Si dentro del grupo el head-to-head tampoco separa (mini-tabla igual), marca
// esas filas con tied: true para resaltarlas.
function applyHeadToHead(rows, matches) {
  let i = 0;
  while (i < rows.length) {
    let j = i + 1;
    while (j < rows.length && baseEqual(rows[j], rows[i])) j++;
    if (j - i > 1) {
      const group = rows.slice(i, j);
      const ids = new Set(group.map((r) => r.teamId));
      const mini = miniTable(group, matches, ids);
      group.sort(
        (a, b) =>
          mini[b.teamId].pts - mini[a.teamId].pts ||
          mini[b.teamId].dif - mini[a.teamId].dif ||
          mini[b.teamId].gf - mini[a.teamId].gf
      );
      // Sub-grupos que ni el head-to-head resuelve -> quedan empatados.
      let k = 0;
      while (k < group.length) {
        let l = k + 1;
        while (l < group.length && miniEqual(mini[group[l].teamId], mini[group[k].teamId])) l++;
        if (l - k > 1) for (let x = k; x < l; x++) group[x].tied = true;
        k = l;
      }
      for (let x = i; x < j; x++) rows[x] = group[x - i];
    }
    i = j;
  }
}

// Mini-tabla usando solo los partidos entre los equipos del grupo igualado.
function miniTable(group, matches, ids) {
  const t = {};
  group.forEach((r) => (t[r.teamId] = { pts: 0, dif: 0, gf: 0 }));
  matches.forEach((m) => {
    if (!ids.has(m.home) || !ids.has(m.away)) return;
    // WO de los dos: lo pierden ambos, así que ninguno suma puntos.
    if (m.doble) {
      t[m.home].dif -= GOLES_WO;
      t[m.away].dif -= GOLES_WO;
      return;
    }
    t[m.home].gf += m.hg;
    t[m.home].dif += m.hg - m.ag;
    t[m.away].gf += m.ag;
    t[m.away].dif += m.ag - m.hg;
    if (m.hg > m.ag) t[m.home].pts += 3;
    else if (m.hg < m.ag) t[m.away].pts += 3;
    else {
      t[m.home].pts++;
      t[m.away].pts++;
    }
  });
  return t;
}
